import { BASE_BACKEND_URL, fetchWithAuth } from '../../../auth'
import { useEffect, useRef, useState } from 'react'


const MIN_SEARCH_LENGTH = 3
const SEARCH_DEBOUNCE_MS = 300

const mapBrandToOption = (brand) => ({
  id: brand?.id || brand?.brand_id || brand?.db_ref || brand?.name,
  db_ref: brand?.db_ref || '',
  name: brand?.name || '',
  label: brand?.name || brand?.db_ref || '',
  raw: brand
})

const normalizeBrandResponse = (response) => {
  const rawBrands =
    response?.table ||
    response?.brands ||
    response?.results ||
    response?.data ||
    []

  return Array.isArray(rawBrands) ? rawBrands.map(mapBrandToOption) : []
}

const buildBrandSearchQuery = ({
  clientDbRef,
  salesmanDbRef,
  searchTerm,
  currentBrandDbRefs = []
}) => {
  const query = new URLSearchParams()

  query.set('client_db_ref', clientDbRef)
  query.set('search', searchTerm)

  if (salesmanDbRef) {
    query.set('salesman_db_ref', salesmanDbRef)
  }

  currentBrandDbRefs
    .filter(Boolean)
    .forEach((brandDbRef) => query.append('current_brand_db_refs[]', brandDbRef))

  return query.toString()
}

export const fetchStoreRelationBrandSearch = async ({
  clientDbRef,
  salesmanDbRef,
  searchTerm = '',
  currentBrandDbRefs = [],
  signal
}) => {
  const response = await fetchWithAuth(
    `${BASE_BACKEND_URL}/poc/brands/search?${buildBrandSearchQuery({
      clientDbRef,
      salesmanDbRef,
      searchTerm,
      currentBrandDbRefs
    })}`,
    {
      signal
    }
  )

  return {
    options: normalizeBrandResponse(response),
    meta: {
      alerts: response?.alerts || [],
      errors: response?.errors || [],
      currentBrandsStatus: response?.current_brands_status || []
    }
  }
}

const useStoreRelationBrandSearch = ({
  clientDbRef,
  salesmanDbRef,
  searchTerm,
  currentBrandDbRefs = []
}) => {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState({
    alerts: [],
    errors: [],
    currentBrandsStatus: []
  })
  const abortControllerRef = useRef(null)
  const debounceTimeoutRef = useRef(null)
  const currentBrandRefsKey = currentBrandDbRefs.filter(Boolean).join('|')

  useEffect(() => {
    const normalizedTerm = searchTerm.trim()

    clearTimeout(debounceTimeoutRef.current)

    if (!clientDbRef || normalizedTerm.length < MIN_SEARCH_LENGTH) {
      abortControllerRef.current?.abort()
      setLoading(false)
      setOptions([])
      setMeta({
        alerts: [],
        errors: [],
        currentBrandsStatus: []
      })
      return undefined
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      abortControllerRef.current?.abort()

      const controller = new AbortController()
      abortControllerRef.current = controller

      try {
        setLoading(true)

        const result = await fetchStoreRelationBrandSearch({
          clientDbRef,
          salesmanDbRef,
          searchTerm: normalizedTerm,
          currentBrandDbRefs,
          signal: controller.signal
        })

        setOptions(result.options)
        setMeta(result.meta)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('useStoreRelationBrandSearch error', error)
          setOptions([])
          setMeta({
            alerts: [],
            errors: [],
            currentBrandsStatus: []
          })
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      clearTimeout(debounceTimeoutRef.current)
      abortControllerRef.current?.abort()
    }
  }, [clientDbRef, currentBrandRefsKey, salesmanDbRef, searchTerm])

  return {
    options,
    loading,
    meta
  }
}

export default useStoreRelationBrandSearch
